import { createRouter, createWebHistory } from "vue-router";
import { isAuth } from "@/helpers/auth";
import { useUserStore } from "@/store/userStore";
import { DateTime } from "luxon";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/login",
      name: "login",
      meta: { requiresAuth: false, login: true },
      component: () => import("@/views/Login.vue"),
    },
    {
      path: "/login/support",
      name: "login-support",
      meta: { requiresAuth: false },
      component: () => import("@/views/LoginSupport.vue"),
    },
    {
      path: "/mailhub/oauth/gmail/callback",
      name: "gmail-callback",
      meta: { requiresAuth: false },
      component: () => import("@/views/GmailCallback.vue"),
    },
    {
      path: "/mailhub/oauth/outlook/callback",
      name: "outlook-callback",
      meta: { requiresAuth: false },
      component: () => import("@/views/OutlookCallback.vue"),
    },
    {
      path: "/activate-email",
      name: "activate-email",
      meta: { requiresAuth: false },
      component: () => import("@/views/ActivateEmail.vue"),
    },
    {
      path:"/trial-feedback",
      name:"trial-feedback",
      meta: { requiresAuth: false},
      component: () => import("@/views/TrialFeedback.vue"),
    },
    {
      path: "/plano/sucesso",
      meta: {requiresAuth: false, 
        message: "Plano assinado com sucesso",
        description: "Efetue login para aplicar as atualizações.",
        routeTo: "/login"
        },
      component: ()=> import("@/views/CallbackTemplate.vue"),
    },
    {
      path: "/plano/cancelado",
      meta: {requiresAuth: false, 
        message: "Assinatura cancelada",
        description: "Nenhuma cobrança foi gerada.",
        routeTo: "/login"
        },
         component: ()=> import("@/views/CallbackTemplate.vue"),
    },
    {
      path: "/",
      name: "/",
      redirect: { name: "planejamento" },
      meta: { requiresAuth: true },
      component: () => import("@/views/Navbar.vue"),
      children: [
        {
          path: "planejamento",
          name: "planejamento",
          redirect: { name: "planejamento/dashboard" },
          component: () => import("@/views/sidebars/PlanningSidebar.vue"),
          children: [
            {
              path: "dashboard",
              name: "planejamento/dashboard",
              component: () => import("@/views/planning/Dashboard.vue"),
            },
            {
              path: "valor-futuro",
              children: [
                {
                  path: "sem-aportes",
                  name: "valor-futuro/sem-aportes",
                  component: () =>
                    import(
                      "@/views/planning/future-value/WithoutContributions.vue"
                    ),
                },
                {
                  path: "com-aportes",
                  name: "valor-futuro/com-aportes",
                  component: () =>
                    import(
                      "@/views/planning/future-value/WithContributions.vue"
                    ),
                },
              ],
            },
            {
              path: "aposentadoria",
              children: [
                {
                  path: "aposentadoria",
                  name: "aposentadoria/aposentadoria",
                  component: () =>
                    import("@/views/planning/retirement/Retirement.vue"),
                },
                {
                  path: "quanto-poupar",
                  name: "aposentadoria/quanto-poupar",
                  component: () =>
                    import("@/views/planning/retirement/HowMuchToSave.vue"),
                },
              ],
            },
            {
              path: "plano-com-aporte",
              children: [
                {
                  path: "aporte",
                  name: "plano-com-aporte/aporte",
                  component: () =>
                    import(
                      "@/views/planning/plan-with-contribution/Contribution.vue"
                    ),
                },
                {
                  path: "tempo",
                  name: "plano-com-aporte/tempo",
                  component: () =>
                    import(
                      "@/views/planning/plan-with-contribution/TimePeriod.vue"
                    ),
                },
                {
                  path: "juros",
                  name: "plano-com-aporte/juros",
                  component: () =>
                    import(
                      "@/views/planning/plan-with-contribution/InterestRate.vue"
                    ),
                },
                {
                  path: "valor-presente",
                  name: "plano-com-aporte/valor-presente",
                  component: () =>
                    import(
                      "@/views/planning/plan-with-contribution/InitialAmount.vue"
                    ),
                },
              ],
            },
            {
              path: "plano-sem-aporte",
              children: [
                {
                  path: "tempo",
                  name: "plano-sem-aporte/tempo",
                  component: () =>
                    import(
                      "@/views/planning/plan-without-contribution/TimePeriod.vue"
                    ),
                },
                {
                  path: "juros",
                  name: "plano-sem-aporte/juros",
                  component: () =>
                    import(
                      "@/views/planning/plan-without-contribution/InterestRate.vue"
                    ),
                },
                {
                  path: "valor-presente",
                  name: "plano-sem-aporte/valor-presente",
                  component: () =>
                    import(
                      "@/views/planning/plan-without-contribution/InitialAmount.vue"
                    ),
                },
              ],
            },
            {
              path: "financiamento",
              name: "planejamento/financiamento",
              component: () => import("@/views/planning/Financing.vue"),
            },
            {
              path: "configuracoes",
              name: "planejamento/configuracoes",
              meta: { requiresAuth: true },
              component: () => import("@/views/Configuration.vue"),
            },
          ],
        },
        {
          path: "crm",
          name: "crm",
          meta: { requiresAuth: true },
          redirect: { name: "crm/dashboard" },
          component: () => import("@/views/sidebars/CrmSidebar.vue"),
          children: [
            {
              path: "dashboard",
              name: "crm/dashboard",
              meta: { requiresAuth: true },
              component: () => import("@/views/crm/Dashboard.vue"),
            },
            {
              path: "pipeline",
              name: "crm/pipeline",
              meta: { requiresAuth: true },
              component: () => import("@/views/crm/Pipeline.vue"),
            },
            {
              path: "empresas",
              name: "crm/empresas",
              meta: { requiresAuth: true },
              component: () => import("@/views/crm/Organizations.vue"),
            },
            {
              path: "empresas/:id",
              name: "crm/empresas/:id",
              component: () => import("@/views/crm/OrganizationDetails.vue"),
              meta: { requiresAuth: true },
              redirect: { name: "crm/empresas/:id/feed" },
              children: [
                {
                  path: "feed",
                  name: "crm/empresas/:id/feed",
                  component: () =>
                    import("@/views/crm/organizationDetails/Feed.vue"),
                },
                {
                  path: "detalhes",
                  name: "crm/empresas/:id/detalhes",
                  component: () =>
                    import("@/views/crm/organizationDetails/Details.vue"),
                },
                {
                  path: "negocios",
                  name: "crm/empresas/:id/negocios",
                  component: () =>
                    import("@/views/crm/organizationDetails/Business.vue"),
                },
                {
                  path: "compromissos",
                  name: "crm/empresas/:id/compromissos",
                  component: () =>
                    import("@/views/crm/organizationDetails/CalendarTasks.vue"),
                },
                {
                  path: "notas",
                  name: "crm/empresas/:id/notas",
                  component: () =>
                    import("@/views/crm/organizationDetails/Notes.vue"),
                },
                {
                  path: "tarefas",
                  name: "crm/empresas/:id/tarefas",
                  component: () =>
                    import("@/views/crm/organizationDetails/Tasks.vue"),
                },
                {
                  path: "emails",
                  name: "crm/empresas/:id/emails",
                  component: () =>
                    import("@/views/crm/organizationDetails/Emails.vue"),
                },
                {
                  path: "arquivos",
                  name: "crm/empresas/:id/arquivos",
                  component: () =>
                    import("@/views/crm/organizationDetails/Files.vue"),
                },
              ],
            },
            {
              path: "contatos",
              name: "crm/contatos",
              meta: { requiresAuth: true },
              component: () => import("@/views/crm/Contacts.vue"),
            },
            {
              path: "contatos/:id",
              name: "crm/contatos/:id",
              meta: { requiresAuth: true },
              redirect: { name: "crm/contatos/:id/feed" },
              component: () => import("@/views/crm/ContactDetails.vue"),
              children: [
                {
                  path: "feed",
                  name: "crm/contatos/:id/feed",
                  component: () =>
                    import("@/views/crm/contactDetails/Feed.vue"),
                },
                {
                  path: "detalhes",
                  name: "crm/contatos/:id/detalhes",
                  component: () =>
                    import("@/views/crm/contactDetails/Details.vue"),
                },
                {
                  path: "negocios",
                  name: "crm/contatos/:id/negocios",
                  component: () =>
                    import("@/views/crm/contactDetails/Business.vue"),
                },
                {
                  path: "compromissos",
                  name: "crm/contatos/:id/compromissos",
                  component: () =>
                    import("@/views/crm/contactDetails/CalendarTasks.vue"),
                },
                {
                  path: "notas",
                  name: "crm/contatos/:id/notas",
                  component: () =>
                    import("@/views/crm/contactDetails/Notes.vue"),
                },
                {
                  path: "tarefas",
                  name: "crm/contatos/:id/tarefas",
                  component: () =>
                    import("@/views/crm/contactDetails/Tasks.vue"),
                },
                {
                  path: "emails",
                  name: "crm/contatos/:id/emails",
                  component: () =>
                    import("@/views/crm/contactDetails/Emails.vue"),
                },
                {
                  path: "arquivos",
                  name: "crm/contatos/:id/arquivos",
                  component: () =>
                    import("@/views/crm/contactDetails/Files.vue"),
                },
              ],
            },
            {
              path: "negocios",
              name: "crm/negocios",
              meta: { requiresAuth: true },
              component: () => import("@/views/crm/Business.vue"),
            },
            {
              path: "agenda",
              name: "crm/agenda",
              meta: { requiresAuth: true },
              component: () => import("@/views/crm/Schedule.vue"),
            },
            {
              path: "configuracoes",
              name: "crm/configuracoes",
              meta: { requiresAuth: true },
              component: () => import("@/views/Configuration.vue"),
            },
            {
              path: "kanban",
              name: "crm/kanban",
              meta: { requiresAuth: true },
              component: () => import("@/views/crm/Kanban.vue"),
            },
            {
              path: "personalizar",
              meta: { requiresManager: true },
              children: [
                {
                  path: "negocios",
                  name: "personalizar/negocios",
                  meta: { requiresAuth: true, requiresManager: true },
                  component: () => import("@/views/crm/customize/Business.vue"),
                },
                {
                  path: "contatos",
                  name: "personalizar/contatos",
                  meta: { requiresAuth: true, requiresManager: true },
                  component: () => import("@/views/crm/customize/Contacts.vue"),
                },
                {
                  path: "empresas",
                  name: "personalizar/empresas",
                  meta: { requiresAuth: true, requiresManager: true },
                  component: () =>
                    import("@/views/crm/customize/Organizations.vue"),
                },
                {
                  path: "pipelines",
                  name: "personalizar/pipelines",
                  meta: { requiresAuth: true, requiresManager: true },
                  component: () => import("@/views/crm/customize/Pipelines.vue"),
                },
                {
                  path: "pipelines/:pipelineId/fases",
                  name: "personalizar/pipelines/fases",
                  meta: { requiresAuth: true, requiresManager: true },
                  component: () =>
                    import("@/views/crm/customize/PipelineStages.vue"),
                },
                {
                  path: "kanban",
                  name: "personalizar/kanban",
                  meta: { requiresAuth: true, requiresManager: true },
                  component: () => import("@/views/crm/customize/Kanbans.vue"),
                },
                {
                  path: "kanban/:kanbanId/fases",
                  name: "personalizar/kanban/fases",
                  meta: { requiresAuth: true, requiresManager: true },
                  component: () =>
                    import("@/views/crm/customize/KanbanStages.vue"),
                },
                {
                  path: "produtos",
                  name: "personalizar/produtos",
                  meta: { requiresAuth: true, requiresManager: true },
                  component: () => import("@/views/crm/customize/Products.vue"),
                },
                {
                  path: "tarefas",
                  name: "personalizar/tarefas",
                  meta: { requiresAuth: true, requiresManager: true },
                  component: () => import("@/views/crm/customize/Tasks.vue"),
                },
              ],
            },
            {
              path: "search",
              name: "crm/search",
              meta: { requiresAuth: true },
              component: () => import("@/views/crm/SearchResults.vue"),
            },
            {
              path: "automacao/:automationId",
              name: "crm/automacao/:automationId",
              meta: { requiresAuth: true },
              component: () =>
                import("@/views/crm/automation/AutomationCreate.vue"),
            },
            {
              path: "crm/automacao/criar",
              name: "crm/automacao/criar",
              meta: { requiresAuth: true },
              component: () =>
                import("@/views/crm/automation/AutomationCreate.vue"),
            },
            {
              path: "automacao",
              name: "crm/automacao",
              meta: { requiresAuth: true },
              component: () =>
                import("@/views/crm/automation/AutomationHome.vue"),
            },
          ],
        },
        {
          path: "analise-de-ativos",
          name: "analise-de-ativos",
          meta: { requiresAuth: true },
          component: () => import("@/views/sidebars/AssetsSidebar.vue"),
          redirect: {name: "outros/historico-de-ativos", query:{
            ativos: "08.070.838/0001-63;PETR4",
            periodicidade: "daily",
            direcao_data: "asc",
            casas_decimais: 2,            
          }},
          children: [
            {
              path: "outros",
              name: "analise-de-ativos/outros",
              children: [
                {
                  path: "historico-de-ativos",
                  name: "outros/historico-de-ativos",
                  component: () => import("@/views/assets/AssetsHistory.vue"),
                },
                {
                  path: "listar-ativos",
                  name: "outros/listar-ativos",
                  component: () => import("@/views/assets/ListAssets.vue"),
                },
              ],
            },
            {
              path: "fundos",
              name: "analise-de-ativos/fundos",
              children: [
                {
                  path: "comparativo-de-fundos",
                  name: "fundos/comparativo-de-fundos",
                  component: () =>
                    import("@/views/assets/funds/FundSheetsComparison.vue"),
                  meta: { title: "Lâmina Comparativa de Fundos" },
                },
                {
                  path: "laminas-de-fundo",
                  name: "fundos/laminas-de-fundo",
                  component: () =>
                    import("@/views/assets/funds/FundSheets.vue"),
                  meta: { title: "Lâmina de Fundo" },
                },
                {
                  path: "captacoes-e-resgates",
                  name: "fundos/captacoes-e-resgates",
                  component: () =>
                    import(
                      "@/views/assets/funds/CollectionsAndRedemptions.vue"
                    ),
                },
                {
                  path: "perfil-mensal",
                  name: "fundos/perfil-mensal",
                  component: () =>
                    import("@/views/assets/funds/MonthlyProfile.vue"),
                  meta: { title: "Perfil Mensal" },
                },
                {
                  path: "ranking-de-fundos",
                  name: "fundos/ranking-de-fundos",
                  component: () =>
                    import("@/views/assets/funds/FundRanking.vue"),
                },
                {
                  path: "lamina-completa",
                  name: "fundos/lamina-completa",
                  component: () =>
                    import("@/views/assets/funds/FundSheetComplete.vue"),
                  meta: { title: "Lâmina Completa" },
                },
                {
                  path: "ranking-de-gestoras",
                  name: "fundos/ranking-de-gestoras",
                  component: () =>
                    import("@/views/assets/funds/ManagerRanking.vue"),
                },
                {
                  path: "historico-de-indicadores",
                  name: "fundos/historico-de-indicadores",
                  component: () =>
                    import("@/views/assets/funds/FundIndicatorHistory.vue"),
                  meta: { title: "Histórico de Indicadores" },
                },
                {
                  path: "explosao-de-carteira",
                  name: "fundos/explosao-de-carteira",
                  component: () =>
                    import(
                      "@/views/assets/funds/fund-portfolio/FundPortfolioExplosion.vue"
                    ),
                  meta: { title: "Explosão de Carteira" },
                },
                {
                  path: "lamina-de-gestoras",
                  name: "fundos/lamina-de-gestoras",
                  component: () =>
                    import("@/views/assets/funds/ManagerSheet.vue"),
                },
                {
                  path: "lista-de-gestoras",
                  name: "fundos/lista-de-gestoras",
                  component: () =>
                    import("@/views/assets/funds/ManagerList.vue"),
                },
                {
                  path: "comparacao-de-indicadores",
                  name: "fundos/comparacao-de-indicadores",
                  component: () =>
                    import("@/views/assets/funds/FundIndicatorComparison.vue"),
                  meta: { title: "Comparação de Indicadores" },
                },
                {
                  path: "explosao-de-taxa",
                  name: "fundos/explosao-de-taxa",
                  component: () =>
                    import("@/views/assets/funds/FundPortfolioTax.vue"),
                },
                {
                  path: "carteira-de-fundo",
                  name: "fundos/carteira-de-fundo",
                  children: [
                    {
                      path: "carteira-de-fundo",
                      name: "fundos/carteira-de-fundo/carteira-de-fundo",
                      component: () =>
                        import(
                          "@/views/assets/funds/fund-portfolio/FundPortfolio.vue"
                        ),
                      meta: { title: "Carteira de Fundo" },
                    },
                    {
                      path: "compras-e-vendas",
                      name: "fundos/carteira-de-fundo/compras-e-vendas",
                      component: () =>
                        import(
                          "@/views/assets/funds/fund-portfolio/FundPortfolioPurchaseAndSell.vue"
                        ),
                      meta: { title: "Carteira de Fundo (Compras e Vendas)" },
                    },
                  ],
                },
              ],
            },
            {
              path: "acoes",
              name: "analise-de-ativos/acoes",
              children: [
                {
                  path: "lamina",
                  name: "acoes/lamina",
                  component: () => import("@/views/assets/stocks/StockSheet.vue"),
                },
                {
                  path: "comparacao-de-indicadores",
                  name: "acoes/comparacao-de-indicadores",
                  component: () => import("@/views/assets/stocks/StockIndicatorComparison.vue"),
                  meta: { title: "Comparação de Indicadores" },
                }
              ],
            },
            {
              path: "fiis",
              name: "analise-de-ativos/fiis",
              children: [
                {
                  path: "lamina",
                  name: "fiis/lamina",
                  component: () => import("@/views/assets/realEstateFund/RealStateFundSheet.vue"),
                },
                {
                  path: "comparacao-de-indicadores",
                  name: "fiis/comparacao-de-indicadores",
                  component: () => import("@/views/assets/realEstateFund/RealStateFundIndicatorComparison.vue"),
                  meta: { title: "Comparação de Indicadores" },
                }
              ],
            },
            {
              path: "renda-fixa",
              name: "analise-de-ativos/renda-fixa",
              children: [
                {
                  path: "laminas",
                  name: "renda-fixa/laminas",
                  children: [
                    {
                      path: "certificados",
                      name: "laminas/certificados",
                      component: () =>
                        import("@/views/assets/fixed-income/sheets/Certificates.vue"),
                    },
                    {
                      path: "titulos-publicos",
                      name: "laminas/titulos-publicos",
                      component: () =>
                        import("@/views/assets/fixed-income/sheets/Securities.vue"),
                    },
                    {
                      path: "debentures",
                      name: "laminas/debentures",
                      component: () =>
                        import("@/views/assets/fixed-income/sheets/Debentures.vue"),
                    }
                  ]
                },
                {
                  path: "comparacao-de-indicadores",
                  name: "renda-fixa/comparacao-de-indicadores",
                  children: [
                    {
                      path: "certificados",
                      name: "renda-fixa/comparacao-de-indicadores/certificados",
                      meta: { title: "Comparação de Indicadores" },
                      component: () =>
                        import("@/views/assets/fixed-income/indicator-comparison/Certificates.vue"),
                    },
                    {
                      path: "titulos-publicos",
                      name: "renda-fixa/comparacao-de-indicadores/titulos-publicos",
                      meta: { title: "Comparação de Indicadores" },
                      component: () =>
                        import("@/views/assets/fixed-income/indicator-comparison/Securities.vue"),
                    },
                    {
                      path: "debentures",
                      name: "renda-fixa/comparacao-de-indicadores/debentures",
                      meta: { title: "Comparação de Indicadores" },
                      component: () =>
                        import("@/views/assets/fixed-income/indicator-comparison/Debentures.vue"),
                    }
                  ]
                }
              ]
            },
            {
              path: "configuracoes",
              name: "analise-de-ativos/configuracoes",
              meta: { requiresAuth: true },
              component: () => import("@/views/Configuration.vue"),
            },
          ],
        },
        {
          path: "simulador-de-portfolio",
          name: "simulador-de-portfolio",
          meta: { requiresAuth: true },
          redirect: { name: "simulador-de-portfolio/portfolio" },
          component: () => import("@/views/sidebars/PortfolioSimulatorSidebar.vue"),
          children: [
            {
              path: "portfolio",
              name: "simulador-de-portfolio/portfolio",
              component: () =>
                import("@/views/portfolio-simulator/PortfolioCreatePage.vue"),
            },
            {
              path: "portfolio/:id",
              name: "simulador-de-portfolio/portfolio/:id",
              component: () =>
                import("@/views/portfolio-simulator/PortfolioEditPage.vue"),
            },
            {
              path: "portfolio/novo",
              name: "simulador-de-portfolio/portfolio/novo",
              component: () =>
                import("@/views/portfolio-simulator/PortfolioEditPage.vue"),
            },
            {
              path: "portfolio-evolutivo",
              name: "simulador-de-portfolio/portfolio-evolutivo",
              component: () =>
                import(
                  "@/views/portfolio-simulator/EvolutivePortfolioCreatePage.vue"
                ),
            },
            {
              path: "portfolio-evolutivo/:id",
              name: "simulador-de-portfolio/portfolio-evolutivo/:id",
              component: () =>
                import(
                  "@/views/portfolio-simulator/EvolutivePortfolioEditPage.vue"
                ),
            },
            {
              path: "portfolio-evolutivo/novo",
              name: "simulador-de-portfolio/portfolio-evolutivo/novo",
              component: () =>
                import(
                  "@/views/portfolio-simulator/EvolutivePortfolioEditPage.vue"
                ),
            },
            {
              path: "portfolios-sincronizados",
              name: "simulador-de-portfolio/portfolios-sincronizados",
              component: () =>
                import("@/views/portfolio-simulator/SynchronizedPortfolios.vue"),
            },
            {
              path: "politica-de-investimento",
              name: "simulador-de-portfolio/politica-de-investimento",
              component: () =>
                import("@/views/portfolio-simulator/InvestmentPolicy.vue"),
            },
            {
              path: "enquadramento-geral",
              name: "simulador-de-portfolio/enquadramento-geral",
              component: () =>
                import("@/views/portfolio-simulator/GeneralFramingReport.vue"),
            },
            {
              path: "enquadramento",
              name: "simulador-de-portfolio/enquadramento",
              component: () =>
                import("@/views/portfolio-simulator/FramingReport.vue"),
            },
            {
              path: "enquadramento-consultor",
              name: "simulador-de-portfolio/enquadramento-consultor",
              component: () =>
                import("@/views/portfolio-simulator/GeneralAdvisorFramingReport.vue"),
            },
            {
              path: "lamina",
              name: "simulador-de-portfolio/lamina",
              component: () =>
                import("@/views/portfolio-simulator/PortfolioSheet.vue"),
              meta: { title: "Lâmina de Portfólio" },
            },
            {
              path: "lamina-comparativa",
              name: "simulador-de-portfolio/lamina-comparativa",
              component: () =>
                import(
                  "@/views/portfolio-simulator/PortfolioSheetComparison.vue"
                ),
              meta: { title: "Lâmina Comparativa de Portfólio" },
            },
            {
              path: "cadastro-de-estrategias",
              name: "simulador-de-portfolio/cadastro-de-estrategias",
              component: () =>
                import("@/views/portfolio-simulator/ClassificationStrategies.vue"),
            },
            {
              path: "cadastro-de-estrategias/:id",
              name: "simulador-de-portfolio/cadastro-de-estrategias/:id",
              component: () =>
                import("@/views/portfolio-simulator/ClassificationStrategiesEdit.vue"),
            },
            {
              path: "cadastro-de-estrategias/novo",
              name: "simulador-de-portfolio/cadastro-de-estrategias/novo",
              component: () =>
                import("@/views/portfolio-simulator/ClassificationStrategiesEdit.vue"),
            },
            {
              path: "classificacao-de-ativos",
              name: "simulador-de-portfolio/classificacao-de-ativos",
              component: () =>
                import("@/views/portfolio-simulator/AssetClassification.vue"),
            },
            {
              path: "rebalanceamento",
              name: "simulador-de-portfolio/rebalanceamento",
              component: () =>
                import("@/views/portfolio-simulator/Rebalancing.vue"),
            },
            {
              path: "configuracoes",
              name: "simulador-de-portfolio/configuracoes",
              component: () => import("@/views/Configuration.vue"),
            },
            
          ]
        },
        {
          path: "risco",
          name: "risco",
          meta: { requiresAuth: true},  
          component: () => import("@/views/sidebars/RiskSidebar.vue"),
          redirect: {name: "risco/histograma-de-retornos",     
            query: {
            data_inicial: DateTime.now().minus({ months: 6 }).toFormat("yyyy-MM-dd"),
            data_final: DateTime.now().toFormat("yyyy-MM-dd"),
            ativo: "08.070.838/0001-63",
            intervalo: 0.15,
            periodicidade: "daily",
            casas_decimais: 2,
          }},
          children: [
            {
              path: "histograma-de-retornos",
              name: "risco/histograma-de-retornos",
              component: () => import("@/views/risk/ReturnsHistogram.vue"),
            },
            {
              path: "correlacao",
              name: "risco/correlacao",
              component: () => import("@/views/risk/Correlation.vue"),
            },
            {
              path: "drawdown",
              name: "risco/drawdown",
              component: () => import("@/views/risk/Drawdown.vue"),
            },{
              path: "beta",
              name: "risco/beta",
              component: () => import("@/views/risk/Beta.vue"),
            },
            {
              path: "monte-carlo",
              name: "risco/monte-carlo",
              component: () => import("@/views/risk/MonteCarlo.vue"),
            },
            {
              path: "markowitz",
              name: "risco/markowitz",
              component: () => import("@/views/risk/Markowitz.vue"),
            },
            {
              path: "configuracoes",
              name: "risco/configuracoes",
              component: () => import("@/views/Configuration.vue"),
            }
          ]
        },
        {
          path: "carteiras",
          name: "carteiras",
          meta: { requiresAuth: true},
          redirect: { name: "carteiras/carteiras" },
          component: () => import("@/views/sidebars/PortfoliosSidebar.vue"),
          children: [
            {
              path: "carteiras",
              name: "carteiras/carteiras",
              component: () => import("@/views/portfolios/PortfolioList.vue"),
            },
            {
              path: "historico-de-carteira",
              name: "carteiras/historico-de-carteira",
              component: () => import("@/views/portfolios/PortfolioHistory.vue"),
            },
            {
              path: "configuracoes",
              name: "carteiras/configuracoes",
              component: () => import("@/views/Configuration.vue"),
            },
            {
              path: "cadastro",
              name: "carteiras/cadastro",
              children: [
                {
                  path: "receita",
                  name: "carteiras/cadastro/receita",
                  component: () => import("@/views/portfolios/register/Income.vue"),
                },
                {
                  path: "cliente",
                  name: "carteiras/cadastro/cliente",
                  component: () => import("@/views/portfolios/register/Client.vue"),
                },
                {
                  path: "custodiante",
                  name: "carteiras/cadastro/custodiante",
                  component: () => import("@/views/portfolios/register/Custodian.vue"),
                },
                {
                  path: "carteira-ativos",
                  name: "carteiras/cadastro/carteira-ativos",
                  component: () => import("@/views/portfolios/consolidation/WalletRegister.vue"),
                },
                {
                  path: "carteira",
                  name: "carteiras/cadastro/carteira",
                  component: () => import("@/views/portfolios/register/Portfolio.vue"),
                },
                {
                  path: "carteira/:id",
                  name: "carteiras/cadastro/carteira/:id",
                  component: () => import("@/views/portfolios/register/PortfolioEdit.vue"),
                },
                {
                  path: "carteira/novo",
                  name: "carteiras/cadastro/carteira/novo",
                  component: () => import("@/views/portfolios/register/PortfolioEdit.vue"),
                },
                {
                  path: "operacoes",
                  name: "carteiras/cadastro/operacoes",
                  component: () => import("@/views/portfolios/consolidation/Operations.vue"),
                },
              ],
            },
            {
              path: "extrato",
              name: "carteiras/extrato",
              children:[{
                path: "imoveis",
                name: "carteiras/extrato/imoveis",
                component: () => import("@/views/portfolios/illiquid/sheets/RealEstateSheet.vue")
            }]
            },
            {
              path: "extrato-completo",
              name: "carteiras/extrato-completo",
              component: () => import("@/views/portfolios/consolidation/ExtratoCompleto.vue"),
            },
            {
              path: "relatorios",
              name: "carteiras/relatorios",
              children: [
                {
                  path: "receita",
                  name: "carteiras/relatorios/receita",
                  children: [
                    {
                      path: "geral",
                      name: "carteiras/relatorios/receita/geral",
                      component: () => import("@/views/portfolios/reports/GeneralReport.vue"),
                    },
                    {
                      path: "carteira",
                      name: "carteiras/relatorios/receita/carteira",
                      component: () => import("@/views/portfolios/reports/PortfolioReport.vue"),
                    },
                    {
                      path: "cliente",
                      name: "carteiras/relatorios/receita/cliente",
                      component: () => import("@/views/portfolios/reports/PortfolioClientReport.vue"),
                    },
                    {
                      path: "clientes",
                      name: "carteiras/relatorios/receita/clientes",
                      component: () => import("@/views/portfolios/reports/GeneralPortfolioClientReport.vue"),
                    },
                    {
                      path: "carteiras",
                      name: "carteiras/relatorios/receita/carteiras",
                      component: () => import("@/views/portfolios/reports/GeneralPortfolioReport.vue"),
                    },
                    {
                      path: "consultor",
                      name: "carteiras/relatorios/receita/consultor",
                      component: () => import("@/views/portfolios/reports/GeneralAdvisorReport.vue"),
                    },
                  ]
                },
                {
                  path: "caixa",
                  name: "carteiras/relatorios/caixa",
                  children: [{
                    path: "carteira",
                    name: "carteiras/relatorios/caixa/carteira",
                    component: () => import("@/views/portfolios/reports/cash-reports/GeneralPortfolioCashReport.vue"),
                  },{
                    path: "clientes",
                    name: "carteiras/relatorios/caixa/clientes",
                    component: () => import("@/views/portfolios/reports/cash-reports/GeneralPortfolioClientCashReport.vue"),
                  },{
                    path: "consultor",
                    name: "carteiras/relatorios/caixa/consultor",
                    component: () => import("@/views/portfolios/reports/cash-reports/GeneralAdvisorCashReport.vue"),
                  },{
                    path: "geral",
                    name: "carteiras/relatorios/caixa/geral",
                    component: () => import("@/views/portfolios/reports/cash-reports/GeneralCashReport.vue"),
                  }
                  ]
                },
                {
                  path: "nnm",
                  name: "carteiras/relatorios/nnm",
                  children: [
                    {
                      path: "geral",
                      name: "carteiras/relatorios/nnm/geral",
                      component: () => import("@/views/portfolios/reports/nnm-reports/GeneralNNMReport.vue"),
                    },
                    {
                      path: "clientes",
                      name: "carteiras/relatorios/nnm/clientes",
                      component: () => import("@/views/portfolios/reports/nnm-reports/GeneralPortfolioClientNNMReport.vue"),
                    },
                    {
                      path: "consultor",
                      name: "carteiras/relatorios/nnm/consultor",
                      component: () => import("@/views/portfolios/reports/nnm-reports/GeneralAdvisorNNMReport.vue"),
                    },
                    {
                      path: "carteiras",
                      name: "carteiras/relatorios/nnm/carteiras",
                      component: () => import("@/views/portfolios/reports/nnm-reports/GeneralPortfolioNNMReport.vue"),
                    }
                  ]
                }
              ]
            },
            {
              path: "iliquidos",
              name: "carteiras/iliquidos",
              children: [
                {
                  path: "carteiras",
                  name: "carteiras/iliquidos/carteiras",
                  component: () => import("@/views/portfolios/illiquid/IlliquidWallets.vue"),
                },
                {
                  path: "imoveis",
                  name: "carteiras/iliquidos/imoveis",
                  component: () => import("@/views/portfolios/illiquid/RealEstate.vue"),
                },
                {
                  path: "imoveis/novo",
                  name: "carteiras/iliquidos/imoveis/novo",
                  component: () => import("@/views/portfolios/illiquid/RealEstateEdit.vue"),
                },
                {
                  path: "imoveis/:id",
                  name: "carteiras/iliquidos/imoveis/:id",
                  component: () => import("@/views/portfolios/illiquid/RealEstateEdit.vue"),
                },
                {
                  path: "veiculos",
                  name: "carteiras/iliquidos/veiculos",
                  component: () => import("@/views/portfolios/illiquid/Vehicles.vue"),
                },
                {
                  path: "veiculos/novo",
                  name: "carteiras/iliquidos/veiculos/novo",
                  component: () => import("@/views/portfolios/illiquid/VehicleEdit.vue"),
                },
                {
                  path: "veiculos/:id",
                  name: "carteiras/iliquidos/veiculos/:id",
                  component: () => import("@/views/portfolios/illiquid/VehicleEdit.vue"),
                },
                {
                  path: "outros",
                  name: "carteiras/iliquidos/outros",
                  component: () => import("@/views/portfolios/illiquid/IlliquidAssets.vue"),
                },
                {
                  path: "outros/novo",
                  name: "carteiras/iliquidos/outros/novo",
                  component: () => import("@/views/portfolios/illiquid/IlliquidAssetEdit.vue"),
                },
                {
                  path: "outros/:id",
                  name: "carteiras/iliquidos/outros/:id",
                  component: () => import("@/views/portfolios/illiquid/IlliquidAssetEdit.vue"),
                }
              ]
            }
          ]
        },
      ],
    },
    {
      path: "/checkout",
      name: "checkout",
      meta: { requiresAuth: true },
      component: () => import("@/views/Checkout.vue"),
    },
    {
      path: "/billing/success",
      name: "billing-success",
      meta: { requiresAuth: true },
      component: () => import("@/views/BillingSuccess.vue"),
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: () => import("@/views/PageNotFound.vue"),
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  let auth = await isAuth();
  if (to.meta.requiresAuth && !auth) {
    next("/login");
  } else {
    next();
  }
});

export default router;
